import { List } from "@solomon-apps/shared/src/components/Data/List";
import { ListRail } from "@solomon-apps/shared/src/components/ListRail";
import { PageHeader } from "@solomon-apps/shared/src/components/PageHeader";
import { RailHeader } from "@solomon-apps/shared/src/components/RailHeader";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ToggleMenu,
    ToggleMenuItem
} from "@solomon-apps/shared/src/components/ui/toggle-menu";
import { useIsMobileViewport } from "@solomon-apps/shared/src/hooks/useIsMobileViewport";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useClientFundingSources } from "@/hooks/useClientFundingSources";
import { useClientPrograms } from "@/hooks/useClientPrograms";
import { clientProgramViewsToClientRunPrograms } from "@/services/clientProgramPresentation";
import type { ClientFundingSource } from "@/types/programCenteredYaas";

import {
    PERIOD_OPTIONS,
    type Policy,
    type Program,
    type ProgramTab,
    REGION_OPTIONS
} from "../shared/programMocks";

import type { FundingAccount } from "./fundingMocks";
import { useActivateClientProgram } from "./hooks/useActivateClientProgram";
import { ProgramDetail } from "./ProgramDetail";
import { mergeClientProgramRailState } from "./programListState";
import { ProgramRow } from "./ProgramRow";

const TABS: { value: ProgramTab; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "ended", label: "Ended" },
    { value: "draft", label: "Draft" }
];

/**
 * Viewport width (px) below which the list + detail collapse into a
 * single-screen master-detail flow. Matches the point where the detail panel
 * would otherwise fall to ~256px (`@3xs`) beside the list rail.
 */
const SINGLE_SCREEN_BREAKPOINT = 576;

function shortReference(address: string): string {
    if (address.length <= 14) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function fundingAccountFromSource(source: ClientFundingSource): FundingAccount {
    const balance = Number(source.balance);
    const assetSuffix = source.asset ? ` · ${source.asset.symbol}` : "";
    return {
        id: source.uuid,
        name: source.label,
        reference: `${shortReference(source.address)}${assetSuffix}`,
        programIds: [],
        balance: Number.isFinite(balance) ? balance : 0,
        projectedNext: 0,
        accruedPending: 0
    };
}

/**
 * Programs — the flagship "Create Program" surface (Use Case 1: Agent
 * Wallet Settlement Rewards). A list rail of seeded programs on the
 * left (status-square + corridor), and a fully interactive program
 * builder on the right for the selected program: configuration selects,
 * slider/select-driven policies that update their own copy live, an
 * add/remove policy affordance, and a program budget input.
 */
/** Config a draft program's builder commits when its program is activated. */
export interface BuilderConfig {
    name: string;
    regions: string[];
    target: "agents" | "holders";
    programType: "fixed" | "continuous";
    budgetMode: "fixed" | "open";
    /** Open-budget / continuous: variable earned APY vs a fixed base APY. */
    apyMode: "earned" | "base";
    baseApy: number;
    rewardsPct: number;
    periodDays: number;
    settlementCycleLength: number;
    settlementCycleUnit: "days" | "weeks";
    budget: number;
    policies: Policy[];
    policyValues: Record<string, number | string>;
}

/** Map a free-form day count to the closest period bucket value. */
function periodValueForDays(days: number): string {
    const match = PERIOD_OPTIONS.reduce((best, option) =>
        Math.abs(option.days - days) < Math.abs(best.days - days)
            ? option
            : best
    );
    return match.value;
}

let newProgramCounter = 0;

/** Build a fresh draft program with sensible, render-safe defaults. */
function createDraftProgram(): Program {
    newProgramCounter += 1;
    return {
        id: `new-program-${Date.now()}-${newProgramCounter}`,
        name: "New Program",
        subtitle: "New incentive program — not yet launched",
        status: "in_progress",
        statusLabel: "Draft",
        region: REGION_OPTIONS[0].value,
        target: "agents",
        period: "3m",
        // A fresh draft opens as a Continuous · Variable (earned APY) program:
        // Program Type = Continuous, Budget Type = Variable, User % = 100.
        programType: "continuous",
        budgetMode: "open",
        // Variable earned APY (0 base APY reads as "earned" in the builder).
        baseApy: 0,
        rewardsPct: 100,
        userPct: 100,
        periodDays: 90,
        settlementCycleLength: 14,
        settlementCycleUnit: "days",
        policies: [],
        budget: 100000
    };
}

export function Programs(): React.ReactElement {
    // `?id=…` deep-link from elsewhere in the app (e.g. the Dashboard's
    // program heading) — pre-select that program instead of defaulting to the
    // first row. Validated against the live list once it arrives (the merge
    // effect below falls back to the first live program on a stale id).
    const [searchParams] = useSearchParams();
    const initialSelectedId = searchParams.get("id") ?? "";

    const [tab, setTab] = useState<ProgramTab>("all");
    const { data: clientProgramData, programs: assignedProgramViews } =
        useClientPrograms();
    const { fundingSources } = useClientFundingSources();
    const livePrograms = useMemo(
        () => clientProgramViewsToClientRunPrograms(assignedProgramViews),
        [assignedProgramViews]
    );
    const fundingAccounts = useMemo(
        () => fundingSources.map(fundingAccountFromSource),
        [fundingSources]
    );
    const [programs, setPrograms] = useState<Program[]>([]);
    const [selectedId, setSelectedId] = useState<string>(initialSelectedId);
    const [draftParentById, setDraftParentById] = useState<
        Record<string, string>
    >({});

    // Below 576px the list and detail no longer fit side by side, so the page
    // becomes a single-screen master-detail: the list fills the viewport, and
    // selecting a program pushes to a full-screen detail with a back affordance.
    const isSingleScreen = useIsMobileViewport(SINGLE_SCREEN_BREAKPOINT);
    const [detailOpen, setDetailOpen] = useState(false);

    // Entering single-screen mode always lands on the list; leaving it drops
    // the drill-in state so the desktop two-column view shows both panes.
    useEffect(() => {
        setDetailOpen(false);
    }, [isSingleScreen]);

    // Selecting a row updates the detail and, on single-screen, drills into it.
    const handleSelect = useCallback((id: string): void => {
        setSelectedId(id);
        setDetailOpen(true);
    }, []);

    // Mock (no-parent) path: commit the activated draft into the local rail
    // state in place — status → active + the config fields folded in.
    const commitActivatedProgram = useCallback(
        (programId: string, config: BuilderConfig): void => {
            setPrograms((prev) =>
                prev.map((p) =>
                    p.id === programId
                        ? {
                              ...p,
                              name: config.name.trim() || p.name,
                              status: "active",
                              statusLabel: "Active",
                              region: config.regions[0] ?? p.region,
                              target: config.target,
                              period: periodValueForDays(config.periodDays),
                              programType: config.programType,
                              budgetMode: config.budgetMode,
                              baseApy: config.baseApy,
                              rewardsPct: config.rewardsPct,
                              // Variable earned budget: the rewards share IS
                              // the User %, so the read summary reflects it.
                              userPct: config.rewardsPct,
                              periodDays: config.periodDays,
                              settlementCycleLength:
                                  config.settlementCycleLength,
                              settlementCycleUnit: config.settlementCycleUnit,
                              budget: config.budget,
                              policies: config.policies,
                              daysElapsed: 0
                          }
                        : p
                )
            );
            setSelectedId(programId);
            // Keep the just-activated program visible if the user is on a tab
            // that would hide it.
            setTab((prev) =>
                prev === "ended" || prev === "draft" ? "active" : prev
            );
        },
        []
    );

    // Live path: once the draft's live program is created, drop the draft row
    // from the rail and forget its parent mapping.
    const onDraftRemoved = useCallback((programId: string): void => {
        setPrograms((prev) =>
            prev.filter((program) => program.id !== programId)
        );
        setDraftParentById((prev) => {
            const next = { ...prev };
            delete next[programId];
            return next;
        });
    }, []);

    // Live path: select the created live program and nudge the visible tab so
    // it isn't hidden behind an Ended/Draft filter.
    const onProgramCreated = useCallback((createdProgramUuid: string): void => {
        setSelectedId(createdProgramUuid);
        setTab((prev) =>
            prev === "ended" || prev === "draft" ? "active" : prev
        );
    }, []);

    const parentProgramUuidFor = useCallback(
        (programId: string): string | undefined => draftParentById[programId],
        [draftParentById]
    );

    const { activate, activationError, pendingCreateDraftIds } =
        useActivateClientProgram({
            parentProgramUuidFor,
            commitActivatedProgram,
            onDraftRemoved,
            onProgramCreated
        });

    useEffect(() => {
        if (!clientProgramData) return;
        setPrograms((prev) =>
            mergeClientProgramRailState(
                prev,
                livePrograms,
                pendingCreateDraftIds
            )
        );
        setSelectedId((current) =>
            !pendingCreateDraftIds.has(current) &&
            (livePrograms.some((program) => program.id === current) ||
                current.startsWith("new-program-"))
                ? current
                : (livePrograms[0]?.id ?? "")
        );
    }, [clientProgramData, livePrograms, pendingCreateDraftIds]);

    const visible = programs
        .filter((p) =>
            tab === "all"
                ? true
                : tab === "active"
                  ? p.status === "active"
                  : tab === "ended"
                    ? p.status === "ended"
                    : p.status === "in_progress"
        )
        // Ended (disabled) programs sink to the bottom of the rail; everything
        // else keeps its original order (stable sort).
        .sort(
            (a, b) =>
                Number(a.status === "ended") - Number(b.status === "ended")
        );

    const selected =
        visible.find((p) => p.id === selectedId) ?? visible[0] ?? null;

    function handleCreateNew(): void {
        const draft = createDraftProgram();
        const parent = assignedProgramViews.find(
            (view) => view.canCreateClientRunProgram
        );
        setPrograms((prev) => [draft, ...prev]);
        if (parent) {
            setDraftParentById((prev) => ({
                ...prev,
                [draft.id]: parent.program.uuid
            }));
        }
        setSelectedId(draft.id);
        // Drafts only show under "All" and "Draft" — switch off Active/Ended
        // so the new draft (and its builder) is actually visible.
        setTab((prev) =>
            prev === "active" || prev === "ended" ? "all" : prev
        );
    }

    // Left rail — composes the generic ListRail: a RailHeader (count +
    // create), a ToggleMenu status filter, and the program rows. All domain
    // content is passed in as slots. Full-bleed on single-screen; a fixed
    // rail (208→320px) beside the detail on wider viewports.
    const listRail = (
        <ListRail
            className={isSingleScreen ? "w-full border-r-0" : "w-52 md:w-80"}
            header={
                <RailHeader
                    // Narrow rail (< @2xs/rail = 288px): stack the count over
                    // the button so neither is squeezed; inline row when wider
                    // (or full-width in single-screen mode).
                    className="flex-col items-start justify-start gap-2 @2xs/rail:flex-row @2xs/rail:items-center @2xs/rail:justify-between @2xs/rail:gap-4"
                    count={<ProgramCount count={visible.length} />}
                    action={
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="xs"
                            onClick={handleCreateNew}
                        >
                            +New Program
                        </Button>
                    }
                />
            }
            filter={
                // Full-width + wrap so the status tabs flow onto a second line
                // (e.g. "Draft" drops below) when the rail is too narrow to hold
                // them in one row.
                <ToggleMenu
                    className="w-full flex-wrap"
                    value={tab}
                    onChange={(next) => next && setTab(next)}
                    spacing={1}
                >
                    {TABS.map((t) => (
                        <ToggleMenuItem
                            key={t.value}
                            value={t.value}
                            variant="toggle"
                            size="sm"
                        >
                            {t.label}
                        </ToggleMenuItem>
                    ))}
                </ToggleMenu>
            }
        >
            <List
                selectable="single"
                selected={selected?.id ?? ""}
                onSelect={handleSelect}
                state={visible.length === 0 ? "empty" : "populated"}
                empty={
                    <div className="px-4 py-4">
                        <Text variant="small" className="text-foreground-muted">
                            No programs in this view.
                        </Text>
                    </div>
                }
            >
                {visible.map((p) => (
                    <ProgramRow key={p.id} program={p} />
                ))}
            </List>
        </ListRail>
    );

    // Detail — the program builder. On single-screen it carries a back
    // affordance that returns to the list.
    const detailPane = selected ? (
        <ProgramDetail
            key={selected.id}
            program={selected}
            fundingAccounts={fundingAccounts}
            activationError={activationError(selected.id) ?? null}
            onActivate={activate}
            onBack={
                isSingleScreen ? (): void => setDetailOpen(false) : undefined
            }
        />
    ) : (
        <div className="flex-1 flex items-center justify-center">
            <Text variant="body" className="text-foreground-muted">
                Select a program to view its configuration.
            </Text>
        </div>
    );

    // Single-screen: the list and detail are stacked screens that slide
    // horizontally (list ← detail) like a master-detail push. The page header
    // hides on the detail screen so the program title leads.
    const slideTransition = {
        type: "tween" as const,
        ease: [0.16, 1, 0.3, 1] as const,
        duration: 0.28
    };

    return (
        <div className="flex flex-col h-full min-h-0">
            {!(isSingleScreen && detailOpen) && (
                <PageHeader title="Programs" className="shrink-0" />
            )}

            {isSingleScreen ? (
                <div className="relative flex-1 min-h-0 overflow-hidden">
                    <AnimatePresence initial={false}>
                        {detailOpen ? (
                            <motion.div
                                key="detail"
                                className="absolute inset-0 flex"
                                initial={{ x: "100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "100%" }}
                                transition={slideTransition}
                            >
                                {detailPane}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="list"
                                className="absolute inset-0 flex"
                                initial={{ x: "-100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "-100%" }}
                                transition={slideTransition}
                            >
                                {listRail}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            ) : (
                <div className="flex flex-1 min-h-0">
                    {listRail}
                    {detailPane}
                </div>
            )}
        </div>
    );
}

function ProgramCount({ count }: { count: number }): React.ReactElement {
    return (
        <Text variant="small" className="text-foreground-muted">
            {count} program{count === 1 ? "" : "s"}
        </Text>
    );
}
